import axios from 'axios';
import { REST_HOSTNAME } from '../Config';
import { reportEndedSessions } from '../util/session';
import { createVerbaScholaLessonAiApi } from './VerbaScholaLessonAiApi';

export const STUDENT_LESSON_ACCESS_CHANGED = 'verbaschola-student-lesson-access-changed';

function checkStudentLessonAccess(error: unknown): never {
    if (axios.isAxiosError(error) && [403, 404].includes(error.response?.status ?? 0) && typeof window !== 'undefined') {
        window.dispatchEvent(new Event(STUDENT_LESSON_ACCESS_CHANGED));
    }
    throw error;
}

export function isLessonAudioNotReady(error: unknown): boolean {
    return axios.isAxiosError(error) && error.response?.status === 409 && error.response.data?.message === 'lesson_audio_not_ready';
}

export function lessonAuthoringError(error: unknown, fallback: string): string {
    if (!axios.isAxiosError(error)) return fallback;
    const message = error.response?.data?.message ?? error.response?.data?.error_code;
    if (message === 'lesson_audio_not_ready') return 'This edit was not saved. Hide the lesson before adding Chinese audio, then show it again once the audio is ready.';
    if (message === 'speaking_step_has_student_attempts') return 'Learners have started this speaking activity. Duplicate the lesson to change its answers or script lines without disrupting their progress.';
    if (message === 'open_response_has_student_answers') return 'Learners have saved answers for this open-answer activity. Duplicate the lesson to change its type without disrupting their work.';
    if (message === 'lesson_not_draft') return 'Guided editing is available only for draft lessons. Use the manual editor for permitted changes, or duplicate this lesson.';
    if (message === 'student_evidence_exists') return 'Learners have started this lesson. Guided editing cannot change their saved work; use the manual editor for permitted changes, or duplicate the lesson.';
    if (message === 'image_generation_limit') return 'Your picture generation limit for the last 24 hours has been reached. Try again after it resets or upload a picture.';
    if (message === 'image_generation_disabled') return 'AI picture generation is turned off for this site. You can upload a PNG or JPEG picture instead.';
    if (message === 'image_filtered') return 'The image service could not create that picture. Reword the scene or upload a picture.';
    if (message === 'image_slot_attempt_limit') return 'This picture has used its automatic attempts. Change the visual prompt and generate again, or upload a picture.';
    if (message === 'image_model_unavailable') return 'AI picture generation is not available for this site. Ask the site administrator to check model access, or upload a picture.';
    if (message === 'image_storage_unavailable') return 'Picture storage is temporarily unavailable. Try again later.';
    if (message === 'invalid_image') return 'Choose a valid PNG or JPEG picture smaller than 32 MB.';
    if (message === 'invalid_image_asset') return 'Add a picture description of at most 500 characters.';
    if (message === 'invalid_image_brief') return 'Add a clear picture concept and its meaning in this lesson.';
    if (message === 'provider_unavailable' || message === 'image_generation_failed') return 'The picture service is unavailable. Try again later or upload a picture.';
    if (message === 'invalid_image_asset_url' || message === 'image_asset_not_found') return 'This picture does not belong to this lesson or is no longer available.';
    if (message === 'required_picture_copy_unavailable') return 'A picture in the original lesson could not be copied. Replace or remove that picture in the original lesson, then try copying again.';
    if (message === 'exercise_images_not_reviewed') return 'Review and confirm every saved picture before marking this exercise Ready.';
    if (message === 'exercise_pictures_need_review') return 'Review each picture in this exercise, then confirm the current pictures before marking it Ready.';
    if (message === 'exercise_picture_review_stale') return 'An exercise picture or its lesson context changed. Reload the picture checklist and review it again.';
    if (message === 'stale_image_review') return 'This picture or its meaning changed. Reload the picture checklist and review the current version.';
    if (message === 'Invalid speaking option starters') return 'Use no more than 20 response starters, with no more than 1,000 characters each.';
    if (message === 'stale_revision' || message === 'authoring_parent_changed') return 'The saved lesson changed. Refresh before trying again; your local edits are still here.';
    if (['quiz_choices_changed', 'speaking_targets_changed', 'typed_answers_changed', 'conversation_lines_changed'].includes(message))
        return 'The saved answers or lines changed. Refresh this lesson before editing them again; your local edits are still here.';
    if (message === 'course_archived' || message === 'lesson_archived')
        return 'This course or lesson was archived. Refresh before making another change.';
    if (error.response?.status === 409) return 'This saved activity changed. Refresh before trying again; your local edits are still here.';
    return fallback;
}

export function lessonPublicationError(error: unknown): string | null {
    if (!axios.isAxiosError(error)) return null;
    const reason = error.response?.data?.message ?? error.response?.data?.error_code;
    if (reason === 'lesson_creation_requires_draft') return 'Create the lesson as a draft, then complete and mark its plan and exercises Ready before publishing.';
    if (reason === 'lesson_has_no_plan_sections') return 'Add at least one lesson plan section before publishing this lesson.';
    if (reason === 'lesson_plan_not_ready') return 'Complete and mark every lesson plan section and material Ready before publishing.';
    if (reason === 'lesson_has_no_ready_exercises') return 'Add exercises and mark every exercise Ready before publishing this lesson.';
    if (reason === 'lesson_exercises_not_ready') return 'Mark every exercise Ready before publishing, or remove unfinished exercises.';
    if (reason === 'lesson_pictures_need_review') return 'Review the pictures in each affected exercise before publishing.';
    if (reason === 'mark_all_exercises_ready_requires_separate_action') return 'Review the exercises and use Mark all ready before publishing.';
    if (reason === 'lesson_ready_exercise_has_no_playable_steps') return 'Every Ready exercise needs a visible, supported activity before students can use this lesson.';
    if (reason === 'lesson_content_too_large') return 'Students can open at most 200 exercises (1,000 visible activities) in one lesson. Move some exercises to another lesson before publishing.';
    if (reason === 'lesson_audio_not_ready') return 'Audio is still processing. Check the lesson audio readiness and try publishing again.';
    if (reason === 'lesson_instructional_content_not_ready') return 'A teacher note or new-word activity is incomplete. Check its content and linked audio before publishing.';
    if (typeof reason === 'string' && reason.startsWith('lesson_instructional_content_not_ready: ')) return reason.slice('lesson_instructional_content_not_ready: '.length);
    if (reason === 'ai_generation_in_progress' || reason === 'ai_proposal_pending') return 'Finish or dismiss the pending AI work before publishing this lesson.';
    return null;
}

const axiosInstance = axios.create({
    baseURL: REST_HOSTNAME
});

// Add request interceptor
axiosInstance.interceptors.request.use(
    function (config) {
        const jwt = localStorage.getItem('jwt');
        if (jwt) {
            config.headers['x-access-token'] = jwt; // Add JWT to the headers
        }
        return config;
    },
    function (error) {
        return Promise.reject(error);
    }
);
reportEndedSessions(axiosInstance);

export interface IVerbaScholaCourse {
    id: number;
    owner_teacher_id: number;
    name: string;
    course_number?: string | null;
    description?: string | null;
    semester?: string | null;
    room?: string | null;
    meeting_days?: string[] | null;
    start_time?: string | null;
    signup_link?: string | null;
    signup_code?: string | null;
    is_archived: boolean;
}

export interface IVerbaScholaLesson {
    id: number;
    authoring_revision: number;
    verbaschola_course_id: number;
    title: string;
    description?: string | null;
    brief?: string | null;
    order_index?: number | null;
    status: string;
    duration_minutes?: number | null;
    exercise_duration_minutes?: number | null;
    proficiency_level?: string | null;
    character_set?: string | null;
    vocabulary_target_mode?: string | null;
    grammar_target_mode?: string | null;
    pedagogy_approach?: string | null;
    ai_context_prompt_text?: string | null;
    is_hidden?: boolean;
    is_archived?: boolean;
    is_ever_published?: boolean | null;
}

export interface IVerbaScholaStudentCourse {
    id: number;
    verbaschola_course_id: number;
    student_user_id: number;
    status: string;
    /** The address the invitation was sent to, which need not be the account that accepted it. */
    email?: string | null;
    invite_token?: string | null;
    /** Account name for accepted students, including unclaimed teacher-created accounts. */
    display_name: string | null;
    first_name?: string | null;
    last_name?: string | null;
    /** False while the teacher-created account still has no password, i.e. the student has never signed in. */
    has_joined?: boolean;
    /** Present on create/update responses when an invitation send was attempted. */
    invitation_sent?: boolean | null;
}

export interface IVerbaScholaStudentCourseUpdateResponse {
    success: boolean;
    invitation_sent?: boolean | null;
}

export interface IVerbaScholaTeacherCourse {
    id: number;
    verbaschola_course_id: number;
    teacher_user_id: number;
    status: string;
    invited_by_user_id?: number | null;
    email?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    display_name: string | null;
    /** False while the teacher-created account still has no password, i.e. the teacher has never signed in. */
    has_joined?: boolean;
    /** Present on create responses when an invitation send was attempted. */
    invitation_sent?: boolean | null;
    /** Explicit inviter confirmation that this may grant teacher access to an existing learner account. */
    confirm_teacher_role_grant?: boolean;
}

export interface IVerbaScholaTeacherCourseUpdateResponse {
    success: boolean;
    invitation_sent?: boolean | null;
}

export interface IVerbaScholaLessonPlanSection {
    id: number;
    verbaschola_lesson_id: number;
    order_index?: number | null;
    title: string;
    body?: string | null;
    duration_minutes?: number | null;
    authoring_source?: string | null;
    readiness_status?: string | null;
    section_type?: string | null;
}

export interface IVerbaScholaLessonVocabularyTarget {
    id: number;
    verbaschola_lesson_id: number;
    order_index?: number | null;
    hanzi: string;
    pinyin?: string[] | null;
    meaning?: string | null;
    source: string;
    is_review: boolean;
}

/** Changes to a lesson's target lists, saved with the lesson in one write (the Description page's lists): targets added,
 * saved targets changed (by id, with the fields changed) and saved targets removed, by scope. */
export interface IVerbaScholaTargetChanges {
    vocabulary?: { added: Partial<IVerbaScholaLessonVocabularyTarget>[]; changed: Partial<IVerbaScholaLessonVocabularyTarget>[]; removed: number[] };
    grammar?: { added: Partial<IVerbaScholaLessonGrammarTarget>[]; changed: Partial<IVerbaScholaLessonGrammarTarget>[]; removed: number[] };
}

export interface IVerbaScholaLessonGrammarTarget {
    id: number;
    verbaschola_lesson_id: number;
    order_index?: number | null;
    pattern: string;
    meaning?: string | null;
    example?: string | null;
    source: string;
}

export interface IVerbaScholaLessonExercise {
    expected_authoring_revision?: number;
    id: number;
    verbaschola_lesson_id: number;
    order_index?: number | null;
    session_number?: number | null;
    title: string;
    subtitle?: string | null;
    status: string;
    picture_review_keys?: string[];
    picture_review_required?: boolean;
    picture_reviewed?: boolean;
    picture_review_legacy_unchanged?: boolean;
    duration_minutes?: number | null;
    content_estimated_minutes?: number;
    purpose?: string | null;
    icon_key?: string | null;
    teacher_instructions?: string | null;
    student_instructions?: string | null;
}

export interface IVerbaScholaLessonExerciseStep {
    expected_authoring_revision?: number;
    id: number;
    verbaschola_lesson_exercise_id: number;
    order_index?: number | null;
    class_name: string;
    title: string;
    is_note_to_student: boolean;
    is_hidden: boolean;
    prompt_text?: string | null;
    prompt_pinyin?: string[] | null;
    image_src_url?: string | null;
    new_word?: string | null;
    new_word_info_text?: string | null;
    check_content_json?: IStudentCheckOptions & { correct_response?: string; incorrect_response?: string };
    instructional_content_json?: { english?: string; definition?: string; example_english?: string; example_sentence_id?: number; image_alt?: string;
        display?: Partial<Record<'picture' | 'english' | 'definition' | 'example' | 'info', boolean>> };
    speaking_content_json?: { teacher_note?: string; hint?: string; student_cue?: string; starters?: string[]; display_starters?: boolean; display_info?: boolean;
        blank_texts?: string[]; blank_text_by_line_id?: Record<string, string>; english?: string; display_english?: boolean;
        line_english_by_id?: Record<string, string>; display_labels?: boolean; definition?: string; display_definition?: boolean;
        example?: string; display_example?: boolean };
    flow_content_json?: { intro?: string; teacher_note?: string; options?: Array<{ id: string; title: string; detail: string;
        response: string; emoji?: string; destination: { course_id: number; lesson_id: number; step_id?: number; restart?: boolean } }> };
    open_response_content_json?: { feedback_mode: 'live' | 'deferred'; guidance: string; speaking_hint: string;
        criteria: { role: 'required' | 'suggested' | 'example'; text: string }[] };
    yes_button_text?: string | null;
    no_button_text?: string | null;
    maybe_button_text?: string | null;
    yes_response?: string | null;
    no_response?: string | null;
    maybe_response?: string | null;
    spoken_yes_response?: string | null;
    spoken_no_response?: string | null;
    display_pinyin?: boolean;
    left_match?: string | null;
    right_match?: string | null;
}

export interface IVerbaScholaLessonGeneratedMaterial {
    id: number;
    verbaschola_lesson_id: number;
    order_index?: number | null;
    title: string;
    material_format?: string | null;
    body_text?: string | null;
    asset_s3_key?: string | null;
    authoring_source?: string | null;
    status?: string | null;
}

export interface IVerbaScholaAttemptSoundGrade {
    id?: number;
    verbaschola_student_exercise_attempt_id?: number;
    index: number;
    transcript_char?: string | null;
    pinyin_expected?: string | null;
    pinyin_spoken?: string | null;
    initial_score_grade?: 'good' | 'ok' | 'bad' | null;
    final_score_grade?: 'good' | 'ok' | 'bad' | null;
    tone_score_grade?: 'good' | 'ok' | 'bad' | null;
    alignment_error?: 'speech' | 'silence' | 'extra' | null;
}

export interface IVerbaScholaSpeakingReviewAttempt {
    attempt_id: number;
    student_user_id: number;
    display_name?: string | null;
    email?: string | null;
    verbaschola_lesson_exercise_step_id: number;
    sentence_score_id: number;
    attempt_number: number;
    teacher_feedback?: string | null;
    teacher_reviewed_at?: string | null;
    feedback_sent_at?: string | null;
    student_reviewed_feedback?: boolean;
    submitted_at?: string | null;
    completed_at?: string | null;
    exercise_title?: string | null;
    step_title?: string | null;
    prompt_text?: string | null;
    expected_response?: string | null;
    recording_text?: string | null;
    audio_s3_key?: string | null;
    pinyin_list_spoken?: string[] | null;
    pinyin_list_transcript?: string[] | null;
    pinyin_list_forced?: string[] | null;
    pinyin_expected?: string[] | null;
    score_mean?: number | null;
}

export interface IVerbaScholaSpeakingReviewSaveResponse {
    attempt: unknown;
    sound_grades: IVerbaScholaAttemptSoundGrade[];
}

export interface IVerbaScholaAudioRegenerationResponse {
    /** Number of sentences newly queued; an already-pending replacement is not queued again. */
    sentence_count: number;
}

export interface IVerbaScholaLessonAudioReadiness {
    authoring_revision: number;
    total_sentences: number;
    required_voices_per_sentence: number;
    available: number;
    partial: number;
    pending: number;
    needs_attention: number;
    failed_replacements: number;
    adaptive_failed_voices?: number;
    adaptive_backoff_voices?: number;
    all_ready: boolean;
    authored: {
        total_sentences: number;
        required_voices_per_sentence: number;
        available: number;
        partial: number;
        pending: number;
        needs_attention: number;
        failed_replacements: number;
        adaptive_failed_voices?: number;
        adaptive_backoff_voices?: number;
        all_ready: boolean;
        assets: { sentence_id: number; text: string; status: 'available' | 'partial' | 'pending' | 'needs_attention'; processing_queue?: number | null; ready_voices: number; required_voices: number; replacement_pending: boolean; failed?: boolean; retry_scheduled?: boolean; voice_failures?: { speaker_id: string; failed_attempts: number; retry_after: string | null; has_playable_audio: boolean; replacement_pending: boolean }[] }[];
    };
}

export interface IVerbaScholaStudentLessonSummary {
    id: number;
    title: string;
    order_index: number | null;
    duration_minutes: number | null;
    step_count: number;
    completed_step_count: number;
    completion_percent: number;
}

export interface IVerbaScholaStudentCourseSummary {
    id: number;
    name: string;
    description: string | null;
    lessons: IVerbaScholaStudentLessonSummary[];
}

export interface IVerbaScholaStudentCatalog {
    courses: IVerbaScholaStudentCourseSummary[];
    preferred_lesson_id?: number | null;
}

export interface IStudentMatchTile { id: string; text?: string; image_src_url?: string; image_alt?: string; sentence_id?: number }
export interface IStudentCheckOptions {
    mode?: 'text' | 'picture'; teacher_note?: string; display_audio?: boolean; display_word?: boolean; display_labels?: boolean;
    display_info?: boolean; display_english?: boolean; display_definition?: boolean; display_example?: boolean;
    question_image_src_url?: string; question_image_alt?: string;
}
export interface IStudentCheckExample { text?: string | null; pinyin?: string[] | null; english?: string | null }
export interface IStudentCheckReveal { choice_id: number; text: string; pinyin?: string[] | null; english?: string | null }
export interface IStudentCheckReview { correct_choice_id?: number; choices?: IStudentCheckReveal[]; pairs?: Record<string, string> }
export interface IStudentCheckFeedback {
    choice_id?: number; text?: string; pinyin?: string[] | null; english?: string | null; info?: string | null;
    definition?: string | null; example?: IStudentCheckExample | null; message?: string | null; revealed_choices?: IStudentCheckReveal[];
}
export interface IVerbaScholaStudentChoice {
    display_word?: string | null;
    english?: string | null;
    sentence_id?: number | null;
    image_src_url?: string;
    image_alt?: string;
    id: number;
    choice_text: string;
    choice_pinyin: string[] | null;
}

export interface IVerbaScholaStudentSentence {
    id: number;
    order_index: number | null;
    sentence_id: number;
    speaker_name: string | null;
    text: string;
    pinyin: string[] | null;
    english?: string | null;
}

export interface IStudentSpeakingState {
    answer_text?: string; writing_feedback?: string;
    results?: Record<string, IStudentSpeakingScore>;
    version: number; index: number; stage: 'write' | 'choose' | 'record' | 'listen';
    failed: number; pre_failures: number; heard: number[]; resolved: string[]; complete: boolean;
    target?: IVerbaScholaStudentSentence | null; choice_id?: number; last_grade?: string; last_status?: string;
}
export interface IStudentSpeaking {
    mode: string | null; title: string; ungraded: boolean; feedback_only?: boolean; lines: IVerbaScholaStudentSentence[];
    question?: IVerbaScholaStudentSentence | null; state: IStudentSpeakingState;
    suggestion?: IVerbaScholaStudentSentence | null;
    options: { correct_text?: string; incorrect_text?: string; info?: string; teacher_note?: string; hint?: string; student_cue?: string; starters?: string[]; english?: string; display_english?: boolean;
        display_labels?: boolean; definition?: string; display_definition?: boolean; example?: string; display_example?: boolean };
    choices: { id: number; text: string; image_src_url?: string; image_alt?: string; cue?: string }[];
}
export interface IStudentSpeakingScore {
    id: number; sentence_id: number; score_mean: number | null; passing_grade: string | null; text?: string;
    sentence_score_status?: string; character_scores?: { order_index: number; character: string; expected_pinyin?: string; spoken_pinyin?: string; is_silence?: boolean; initial_grade?: string; final_grade?: string; tone_grade?: string; initial_score?: number; final_score?: number; tone_score?: number }[];
}

export interface IStudentFlow {
    kind: 'summary' | 'select_next_dialog'; intro?: string; prompt?: string; teacher_note?: string | null;
    practice_minutes?: number | null; practice_time_complete?: boolean;
    sentences?: { sentence_id: number; text: string; pinyin: string[] | null; attempts: { attempt_id: number; score_id: number; step_id: number; score_mean: number | null; passing_grade: string | null; character_scores: IStudentSpeakingScore['character_scores'] }[] }[];
    has_more?: boolean;
    unavailable?: boolean;
    options?: { id: string; title: string; detail: string; emoji?: string }[];
    state: { version: number; complete: boolean; selected_option_id?: string; response?: string; destination?: { course_id: number; lesson_id: number; step_id?: number; restart?: boolean } };
}

export interface IVerbaScholaStudentStep {
    speaking?: IStudentSpeaking;
    flow?: IStudentFlow;
    open_response?: IStudentOpenResponse | null;
    review_result?: IStudentCheckReview;
    answer_audio_sentence_id?: number;
    id: number;
    order_index: number | null;
    class_name: 'quiz_question' | 'speak_with_grade' | 'conversation_listen' | 'type_speak' | string;
    title: string;
    is_note_to_student: boolean;
    is_hidden?: boolean;
    prompt_text: string | null;
    prompt_pinyin: string[] | null;
    display_pinyin: boolean | null;
    is_complete: boolean;
    max_attempt_number: number;
    is_supported: boolean;
    is_playable: boolean;
    failed_attempts?: number;
    question_sentence?: IVerbaScholaStudentSentence;
    check_content?: IStudentCheckOptions;
    matching?: { left_view: string; right_view: string; left: IStudentMatchTile[]; right: IStudentMatchTile[] };
    choices?: IVerbaScholaStudentChoice[];
    sentences?: IVerbaScholaStudentSentence[];
    image_src_url?: string | null;
    new_word?: string | null;
    new_word_info_text?: string | null;
    instructional_content?: { english?: string | null; definition?: string | null; example_english?: string | null; image_alt?: string | null; show_example: boolean };
    instructional_example?: IVerbaScholaStudentSentence | null;
    accepted_response_count?: number;
    conversation?: {
        id: number;
        name: string | null;
        sentences: IVerbaScholaStudentSentence[];
    } | null;
}

export interface IStudentOpenResponse {
    kind: 'write_open' | 'speak_open' | 'type_speak_open';
    title: string;
    question: string;
    guidance: string;
    speaking_hint: string;
    feedback_mode: 'live' | 'deferred';
    criteria: { role: 'required' | 'suggested' | 'example'; text: string }[];
}

/** A v37 pronunciation tip; numeric-tone pinyin. Heard pinyin and scorer grades are teacher-only. */
export interface IOpenResponsePronunciationTip {
    text: string;
    intended?: string;
    expected_pinyin: string | null;
    tip: string;
    priority?: number;
    strength?: 'likely' | 'possible';
    heard_pinyin?: string;
    scorer_grades?: string[];
}

/** Where a language note sits in the answer text (UTF-16 offsets); each part is absent when it is not certain. */
export interface IOpenResponseSpanAnchor {
    /** The quoted words, when the occurrence is known. */
    span_start?: number;
    span_end?: number;
    /** The written sentence holding the quote, with place_revision (that sentence with only the quoted words
     *  changed); given only when the correction changes nothing else, so it can be shown in place. */
    place_start?: number;
    place_end?: number;
    place_revision?: string;
    /** The range for pages from before place_*, which replace it with the corrected sentence itself: sent only when
     *  that is safe, and used here only when place_* is absent. */
    sentence_start?: number;
    sentence_end?: number;
    /** An audio-only note: the one recording whose raw pinyin holds the quote. Not a replacement range. */
    recording_start?: number;
    recording_end?: number;
}

/** Live feedback's sound detail for one recording, from the step-8 scorer (numeric-tone pinyin). */
export interface IOpenResponsePronunciation {
    // checked: split into sentences; no_speech: none recognized; unavailable: transcription failed.
    status: 'checked' | 'no_speech' | 'unavailable';
    // script: graded against the student's writing; transcript: against the automatic transcript.
    source: 'script' | 'transcript';
    sentences: {
        index: number;
        // scored, not_in_script, no_speech, target_mismatch, unavailable, invalid_scorer_result, skipped_too_long
        status: string;
        text: string;
        heard_text: string | null;
        start_ms: number | null;
        end_ms: number | null;
        // The whole sentence(s) of the answer text this was recorded from (UTF-16), when it can be placed.
        answer_start: number | null;
        answer_end: number | null;
        characters: NonNullable<IStudentSpeakingScore['character_scores']>;
    }[];
    summary: Record<'initial' | 'final' | 'tone', { good: number; ok: number; bad: number }>;
    patterns: { key: string; label: string; count: number;
        examples: { character: string; expected_pinyin: string; spoken_pinyin: string }[] }[];
}

export interface IStudentOpenResponseAttempt {
    id: number;
    attempt_number: number;
    written_text: string | null;
    has_audio: boolean;
    transcript_text: string | null;
    is_final: boolean;
    feedback_requested: boolean;
    assessment_status: 'queued' | 'processing' | 'feedback_ready' | 'needs_teacher_review' | 'failed';
    feedback: { corrections?: ({ original_span: string; corrected_sentence: string; teaching_moment: string;
        new_example_zh?: string; new_example_en?: string; priority?: number } & IOpenResponseSpanAnchor)[];
        naturalness_suggestions?: ({ original_span: string; suggested_sentence: string; reason: string;
            new_example_zh?: string; new_example_en?: string; priority?: number } & IOpenResponseSpanAnchor)[];
        pronunciation_items?: IOpenResponsePronunciationTip[] } | null;
    pronunciation?: IOpenResponsePronunciation | null;
    // Why the check of the latest draft failed, when recording again can fix it.
    failure_reason?: 'no_transcript' | 'recording_too_short' | 'sentence_too_long' | null;
    grade: IReleasedOpenResponseGrade | null;
    submitted_at: string;
    feedback_released_at: string | null;
}

export interface ITeacherOpenResponseAttempt {
    id: number;
    student_user_id: number;
    student_name: string;
    step_id?: number;
    step_title?: string;
    exercise_title?: string;
    attempt_number: number;
    written_text: string | null;
    has_audio: boolean;
    is_final: boolean;
    assessment_status: string;
    transcript_json: { text: string; source: string } | null;
    feedback_json: IStudentOpenResponseAttempt['feedback'];
    grading_json: { status: string; reason?: string; score?: number | null; speech_error?: string | null;
        speech_evidence_sent?: boolean; speech_evidence?: { recorded_sentences: number; scored_sentences: number;
            statuses: Record<string, number> } | null } | null;
    teacher_grade_json: ITeacherOpenResponseGrade | null;
    submitted_at: string;
    feedback_released_at?: string | null;
    content_snapshot_json?: { kind: string; question: string; guidance?: string; speaking_hint?: string;
        criteria: { role: string; text: string }[]; feedback_mode: string };
}

export interface ITeacherOpenResponseGrade {
    score: number | null;
    level?: 'Developing' | 'Meets' | 'Strong';
    comment: string;
    strengths?: string;
    next_steps?: string;
    reviewed_notes?: { source?: string; text: string }[];
    reviewed_at?: string;
}

export interface IReleasedOpenResponseGrade extends ITeacherOpenResponseGrade {
    score: number;
}

export interface IVerbaScholaStudentExercise {
    id: number;
    order_index: number | null;
    session_number?: number | null;
    title: string;
    subtitle: string | null;
    duration_minutes: number | null;
    icon_key: string | null;
    student_instructions: string | null;
    steps: IVerbaScholaStudentStep[];
    is_playable: boolean;
}

export interface IVerbaScholaPracticeSession {
    number: number;
    estimated_minutes: number;
    items: { exercise_id: number; step_id: number }[];
    activity_types: string[];
    missing_activity_types: string[];
}

export interface IVerbaScholaTargetCoverageRow { key: string; kind: 'vocabulary' | 'grammar'; label: string; practised: boolean }
export interface IVerbaScholaSessionTargetCoverage {
    session_number: number;
    round: number;
    focus: IVerbaScholaTargetCoverageRow[];
    review: IVerbaScholaTargetCoverageRow[];
    missing: string[];
    /** Focus targets that no Ready exercise in this session practises (what its students miss). */
    ready_missing: string[];
}
export interface IVerbaScholaTargetCoverage {
    vocabulary_per_session: number;
    grammar_per_session: number;
    groups_per_round: number;
    planned_session_count: number;
    time_session_count: number;
    sessions_without_exercises: number[];
    has_gaps: boolean;
    /** The exercise target, raised when targets need added sessions. */
    content_goal_minutes: number;
    vocabulary: { total: number; practised: number; missing: string[] };
    grammar: { total: number; practised: number; missing: string[] };
    /** Ready exercises only: what students receive. */
    ready: { vocabulary: { total: number; practised: number; missing: string[] }; grammar: { total: number; practised: number; missing: string[] } };
    /** Every saved session, including one whose exercises are still unfinished; `planned_focus` also covers planned numbers with no exercises. */
    sessions: IVerbaScholaSessionTargetCoverage[];
    /** Planned focus for every session number up to the plan or the highest saved session, playable or not. */
    planned_focus?: { session_number: number; focus: Omit<IVerbaScholaTargetCoverageRow, 'practised'>[]; review?: Omit<IVerbaScholaTargetCoverageRow, 'practised'>[] }[];
}

export interface IVerbaScholaStudentLessonContent {
    course: { id: number; name: string; description: string | null };
    lesson: {
        id: number;
        title: string;
        duration_minutes: number | null;
        proficiency_level: string | null;
        character_set: string | null;
    };
    exercises: IVerbaScholaStudentExercise[];
    practice_sessions?: IVerbaScholaPracticeSession[];
    is_playable: boolean;
    unsupported_step_types: string[];
}

export interface IVerbaScholaStudentFeedbackAttempt {
    attempt_id: number;
    sentence_score_id: number;
    lesson_title: string;
    exercise_title: string;
    step_title: string;
    prompt_text: string | null;
    target_text: string;
    target_pinyin: string[] | null;
    score_mean: number | null;
    has_recording: boolean;
    submitted_at: string;
    feedback_sent_at: string | null;
    teacher_feedback: string | null;
    student_reviewed_feedback: boolean;
    sound_grades: IVerbaScholaAttemptSoundGrade[];
}

export interface IVerbaScholaStudentFeedback {
    attempts: IVerbaScholaStudentFeedbackAttempt[];
    has_more: boolean;
}

export interface IVerbaScholaStudentAttemptResponse {
    speaking?: IStudentSpeaking;
    flow?: IStudentFlow;
    attempt: { id: number; is_correct: boolean | null; sentence_score_id: number | null; is_skipped?: boolean };
    check_feedback?: IStudentCheckFeedback | null;
    review_result?: IStudentCheckReview | null;
    answers: { id: number; verbaschola_lesson_exercise_step_choice_id: number | null; answer_text: string | null; is_correct: boolean | null }[];
    deduplicated: boolean;
    speaking_target: { step_sentence_id: number; sentence_id: number; text: string; pinyin: string[] } | null;
}

export interface IVerbaScholaStudentAttemptPayload {
    speaking_action?: string; state_version?: number; heard_sentence_id?: number; choice_id?: number; answer_text?: string;
    flow_action?: 'complete_summary' | 'choose' | 'complete_unavailable'; option_id?: string;
    is_skipped?: boolean;
    content_viewed?: boolean;
    listened_sentence_ids?: number[];
    id: 0;
    attempt_number: number;
    idempotency_key: string;
    started_at?: string;
    submitted_at?: string;
    completed_at?: string;
    duration_ms?: number;
    sentence_score_id?: number;
    is_correct?: boolean;
    answers: { order_index: number; choice_id?: number; match_token?: string; answer_text?: string; elapsed_ms?: number }[];
}

/** Rollup counters. `accuracy_percent` is null when nothing scoreable has been attempted. */
export interface IVerbaScholaSummary {
    lesson_count?: number;
    student_count: number;
    current_lesson_title?: string | null;
    current_lesson_completion_percent?: number;
    completion_percent: number;
    accuracy_percent: number | null;
    attempt_count: number;
    awaiting_review_count?: number;
}

/**
 * One roster row. `display_name` is the student's account name, falling back to their email;
 * it is null only when the account has neither, in which case the page shows the id.
 */
export interface IVerbaScholaStudentProgress {
    student_user_id: number;
    display_name: string | null;
    email: string | null;
    step_count: number;
    completed_step_count: number;
    completion_percent: number;
    attempt_count: number;
    accuracy_percent: number | null;
}

export interface IVerbaScholaCourseSummary {
    course: IVerbaScholaCourse;
    summary: IVerbaScholaSummary;
}

export interface IVerbaScholaDashboard {
    courses: IVerbaScholaCourseSummary[];
}

export interface IVerbaScholaLessonGroup {
    lesson: IVerbaScholaLesson;
    students: IVerbaScholaStudentProgress[];
    summary: IVerbaScholaSummary;
}

export interface IVerbaScholaLessonExerciseProgress {
    exercise_id: number;
    title: string;
    subtitle?: string | null;
    status: string;
    step_count: number;
    student_count: number;
    completed_student_count: number;
    completion_percent: number;
    accuracy_percent: number | null;
    attempt_count: number;
    speaking_attempt_count: number;
}

export interface IVerbaScholaCourseOverview {
    course: IVerbaScholaCourse;
    lessons: IVerbaScholaLessonGroup[];
    summary: IVerbaScholaSummary;
}

export interface IVerbaScholaLessonOverview {
    lesson: IVerbaScholaLesson;
    students: IVerbaScholaStudentProgress[];
    summary: IVerbaScholaSummary;
    exercises?: IVerbaScholaLessonExerciseProgress[];
}

export interface IVerbaScholaExerciseImageAsset {
    id: number;
    url: string;
    alt_text: string;
    source: 'generated' | 'uploaded';
    model_id?: string | null;
    seed?: number | null;
    candidate_group_key?: string | null;
    image_brief?: IVerbaScholaExerciseImageBrief | null;
    quality_review?: IVerbaScholaExerciseImageQualityReview | null;
    generation_prompt?: string | null;
    generation_warning?: string | null;
    candidates?: IVerbaScholaExerciseImageAsset[];
}

export interface IVerbaScholaExerciseImageBrief {
    concept?: string;
    meaning?: string;
    lesson_context?: string;
    visual_hint?: string;
    distractors?: string[];
    student_task?: string;
}

export interface IVerbaScholaExerciseImageQualityReview {
    score?: number | null;
    review_version?: string;
    image_only_observation?: {
        visible_scene: string;
        identity_features: string;
        visible_text: string;
        text_evidence: string;
        style: string;
        style_evidence: string;
    } | null;
    accepted?: boolean;
    correct?: boolean;
    useful?: boolean;
    status?: string;
    rationale?: string;
    revised_prompt?: string;
    critical_checks?: { name: string; passed: boolean; evidence?: string }[];
}

export interface IVerbaScholaExercisePictureReviewItem {
    key: string;
    url: string | null;
    alt: string | null;
    context: string;
    step_id: number;
    missing?: boolean;
    quality_review?: IVerbaScholaExerciseImageQualityReview | null;
    source?: string | null;
    generation_prompt?: string | null;
    image_brief?: IVerbaScholaExerciseImageBrief | null;
}

export interface IVerbaScholaExercisePictureReview {
    pictures: IVerbaScholaExercisePictureReviewItem[];
    missing_pictures?: IVerbaScholaExercisePictureReviewItem[];
    reviewed: boolean;
    requires_review?: boolean;
    digest?: string;
}

export interface IVerbaScholaExerciseImageCandidates {
    selected_url: string;
    candidates: IVerbaScholaExerciseImageAsset[];
    image_brief?: IVerbaScholaExerciseImageBrief | null;
    next_before_id?: string | null;
}

export interface IVerbaScholaExerciseImageRequest {
    concept: string;
    meaning: string;
    lesson_context?: string;
    visual_hint?: string;
    distractors?: string[];
    variation_key?: string;
    alt_text?: string;
    source_image_url?: string;
    teacher_feedback?: string;
    student_task?: string;
}

export interface IVerbaScholaLessonReviewIssue {
    id: number;
    source: 'plan' | 'exercise' | 'image';
    category: string;
    severity: 'warning' | 'error';
    status: 'open' | 'changed' | 'accepted' | 'resolved' | 'stale';
    title: string;
    detail: string;
    suggestion?: string | null;
    generation_id?: number | null;
    plan_section_id?: number | null;
    generated_material_id?: number | null;
    vocabulary_target_id?: number | null;
    grammar_target_id?: number | null;
    exercise_id?: number | null;
    step_id?: number | null;
    picture_key?: string | null;
    score?: number | null;
    period_number?: number | null;
    action_required?: 'picture_checklist' | 'generation_job' | null;
}

export interface IVerbaScholaPdfPageText {
    /** text: the page's own text layer; text_and_ocr: plus text read in its pictures; corrected_text: the layer corrected
     * where OCR read something else at the same place (pinyin a font draws from Chinese codes); ocr: the whole page read by OCR. */
    method: 'text' | 'text_and_ocr' | 'corrected_text' | 'ocr';
    text: string;
    /** Why the whole page was read by OCR; null when its own text layer was used. */
    ocr_reason: 'no_text_layer' | 'unreadable_text_layer' | 'invisible_text_layer' | 'unreadable_annotations' | 'requested' | null;
    /** OCR readings scored low enough for the teacher to check (none is dropped). */
    uncertain: string[];
    /** Readings that disagree with a dictionary, the page's script or the PDF's own text, or pinyin OCR could not read. */
    checks: { reading: string; note: string }[];
    /** Line numbers (from 1) of text-layer lines holding glyph codes rather than text. */
    garbled_lines: number[];
}

/** A line of a page that a warning is about or rests on: its place (from 0, without the page's mark and note), its text,
 * and whether it was the only line with that text (only such a line is followed when lines move). */
export interface PdfImportAnchor { index: number; line: string; unique: boolean }

/** A finding of the AI page check, placed on the page's text as read. */
export interface IVerbaScholaPdfPageFinding {
    kind: 'ai';
    line_index: number;
    /** The text in the line it is about; null for text missing from the reading, which goes before or after the line. */
    reading: string | null;
    insert: 'before' | 'after' | null;
    /** What the page prints, the likely reading, or the missing text. */
    suggestion: string | null;
    /** page: the picture shows it; language: the picture does not settle it, but the reading is unlikely. */
    evidence: 'page' | 'language';
    why: string;
    /** For missing text: where on the page. */
    region: string | null;
    /** Other lines it rests on. */
    context_indices: number[];
}

/** The AI page check of one page: done in full, in part, failed, or not done, with a note and the model. */
export interface IVerbaScholaPdfPageCheck {
    status: 'checked' | 'partial' | 'failed' | 'not_checked';
    note: string | null;
    model: string | null;
    /** As saved: the version of the page's text the check covered (null when it was not made). */
    version?: string | null;
}

/** A warning from reading a page that "Add pages from a PDF" added, and the teacher's decision on it. */
export interface IVerbaScholaPdfImportWarning {
    id: number;
    page_number: number;
    /** check: a reading the check step questions; ocr: a reading OCR was unsure of; font: a PDF text line in a broken
     * font; ai: a finding of the AI page check. */
    kind: 'check' | 'ocr' | 'font' | 'ai';
    /** The text the warning is about; null when it is about a whole line, or text missing from it. */
    reading: string | null;
    why: string;
    suggestion: string | null;
    /** An AI finding's: page, a possible transcription error; language, a possible Chinese-language issue. */
    evidence: 'page' | 'language' | null;
    insert: 'before' | 'after' | null;
    region: string | null;
    /** Its own line, as read, and the lines it rests on. */
    line_index: number;
    line: string;
    line_unique: boolean;
    context: PdfImportAnchor[];
    /** The version of the page's text its lines were taken on: as read, or as it was when the AI checked it again. */
    base_version?: string;
    /** An AI finding kept from an earlier check because the latest check of its page covered the page only in part. */
    earlier_check?: boolean;
    decision: 'open' | 'looks_right';
    /** Its lines as they were when the teacher marked it as right, and the page's version then; null while open. */
    decided_index: number | null;
    decided_line: string | null;
    decided_unique: boolean | null;
    decided_context: PdfImportAnchor[] | null;
    decided_version: string | null;
}

/** A decision on a warning, as the server takes it: looks right on its lines as the teacher sees them, or open again. */
export type PdfImportDecision = Pick<IVerbaScholaPdfImportWarning, 'decision' | 'decided_index' | 'decided_line' | 'decided_unique' | 'decided_context' | 'decided_version'>;

/** One "Add pages from a PDF" import: the field it went into, its source line (the block's first line adds "(import <id>)"), its pages and warnings. */
export interface IVerbaScholaPdfImport {
    id: number;
    verbaschola_lesson_id: number;
    lesson_field: 'brief' | 'ai_context_prompt_text';
    source_line: string;
    /** Each page: how it was read, its text as read (what the warnings are placed on), its AI check, and whether its picture is kept. */
    pages_json: { page_number: number; label: string | null; method: IVerbaScholaPdfPageText['method']; read_text: string; ai_check: IVerbaScholaPdfPageCheck; picture: boolean }[];
    warnings_json: IVerbaScholaPdfImportWarning[];
}

/**
 * The vocabulary and grammar targets of pages going into the lesson brief, as the AI read them from the pages' text
 * (service/verbaschola_pdf_targets.py), for the teacher to review: each with the page it is on, and whether its pinyin
 * and meaning (a word's) or explanation (a point's) are printed there or the AI's.
 */
/** Where a target came from: the pages' own lists, the words the reading uses, the AI's suggestions, or the teacher. */
export interface PdfTargetEvidence {
    origin?: 'listed' | 'reading' | 'suggested' | 'teacher';
    quote?: string | null;
    /** The retained quote's own page, even when a duplicate target is also found on other pages. */
    quote_page_number?: number;
    source_term?: string;
    translated?: boolean;
    reason?: string;
    example_generated?: boolean;
}

/** A reading of what the pages' text uses ('reading'), or of suggestions for the whole selection ('suggest'); the pages'
 * own lists are read with no options. */
export interface PdfTargetOptions {
    phase: 'reading' | 'suggest';
    /** The teacher's instructions, '' for none. */
    instructions: string;
    level: string;
    character_set: 'simplified' | 'traditional';
    vocabulary_count: number;
    grammar_count: number;
    exclude_vocabulary: string[];
    exclude_grammar: string[];
}

export interface IVerbaScholaPdfTargets {
    /** page_numbers: every page holding the target's quote (a target listed again is one); listed_page_numbers (a target the
     * pages list): the pages known to list it, not those whose prose merely holds its words; listed_pages_unsure: an entry's
     * page is not known (several pages hold it); page_number: the first page its quote is known to be from. */
    vocabulary: (PdfTargetEvidence & { hanzi: string; pinyin: string | null; meaning: string | null; page_number: number; page_numbers?: number[]; listed_page_numbers?: number[];
        listed_pages_unsure?: boolean;
        pinyin_printed: boolean; meaning_printed: boolean; note: string | null })[];
    /** pattern_checked: its Chinese is in the page's words naming it (false: it has none to check). */
    grammar: (PdfTargetEvidence & { pattern: string; explanation: string | null; example: string | null; page_number: number; page_numbers?: number[]; listed_page_numbers?: number[];
        listed_pages_unsure?: boolean;
        pattern_checked: boolean;
        /** example_checked: the example holds the pattern's Chinese (false: the pattern has none, so that it shows the point is the AI's reading). */
        explanation_printed: boolean; example_checked: boolean; note: string | null })[];
    /** Targets the AI gave that the pages do not hold, left out. */
    left_out: number;
    /** The reading reached the most targets one reading lists: it may have stopped before the end. */
    incomplete: boolean;
    model: string;
}

/** A new import as the popup sends it; the server places each warning on the page as read, numbers it and opens it. */
export type PdfImportPayload = Pick<IVerbaScholaPdfImport, 'lesson_field' | 'source_line'> & {
    pages_json: Omit<IVerbaScholaPdfImport['pages_json'][number], 'picture'>[];
    warnings_json: (Pick<IVerbaScholaPdfImportWarning, 'page_number' | 'kind' | 'reading' | 'why' | 'suggestion' | 'evidence' | 'insert' | 'region' | 'line_index'> & { context_indices: number[] })[];
};

export interface IVerbaScholaLessonReviewIssues {
    issues: IVerbaScholaLessonReviewIssue[];
    counts: { open: number; warning: number; error: number };
}

export const VerbaScholaApi = {
    getStudentOpenResponses: (stepId: number) => axiosInstance.get<{ attempts: IStudentOpenResponseAttempt[] }>(`/student/verbaschola/steps/${stepId}/open_responses`).catch(checkStudentLessonAccess),
    submitStudentOpenResponse: (stepId: number, payload: { written_text?: string; source_attempt_id?: number; is_final: boolean; idempotency_key: string }, audio?: Blob) => {
        const data = new FormData();
        data.append('payload', JSON.stringify(payload));
        // The server reads the part's MIME type; the file name only mirrors it.
        const extension = ['mp4', 'ogg', 'wav'].find(type => audio?.type.startsWith(`audio/${type}`)) ?? 'webm';
        if (audio) data.append('audio', audio, `answer.${extension}`);
        return axiosInstance.post<IStudentOpenResponseAttempt>(`/student/verbaschola/steps/${stepId}/open_responses`, data, { timeout: 120000 }).catch(checkStudentLessonAccess);
    },
    getStudentOpenResponseAudio: (stepId: number, attemptId: number) => axiosInstance.get<Blob>(`/student/verbaschola/steps/${stepId}/open_responses/${attemptId}/audio`, { responseType: 'blob' }).catch(checkStudentLessonAccess),
    getTeacherOpenResponses: (stepId: number) => axiosInstance.get<{ attempts: ITeacherOpenResponseAttempt[] }>(`/teacher/verbaschola/steps/${stepId}/open_responses`),
    getTeacherLessonOpenResponses: (lessonId: number) => axiosInstance.get<{ attempts: ITeacherOpenResponseAttempt[] }>(`/teacher/verbaschola/lessons/${lessonId}/open_responses`),
    getTeacherOpenResponseAudio: (stepId: number, attemptId: number) => axiosInstance.get<Blob>(`/teacher/verbaschola/steps/${stepId}/open_responses/${attemptId}/audio`, { responseType: 'blob' }),
    saveTeacherOpenResponseGrade: (stepId: number, attemptId: number, grade: ITeacherOpenResponseGrade & { release: boolean; expected_reviewed_at: string | null }) =>
        axiosInstance.put<{ id: number; grade: ITeacherOpenResponseGrade }>(`/teacher/verbaschola/steps/${stepId}/open_responses/${attemptId}/grade`, grade),
    getStudentSpeakingScore: (stepId: number, scoreId: number) => axiosInstance.request<IStudentSpeakingScore>({
        method: 'GET', url: `/student/verbaschola/steps/${stepId}/speaking_scores/${scoreId}`, timeout: 20000,
    }),
    getStudentSpeakingAudio: (stepId: number, scoreId: number) => axiosInstance.request<Blob>({
        url: `/student/verbaschola/steps/${stepId}/speaking_scores/${scoreId}/audio`, method: 'GET', responseType: 'blob', timeout: 20000,
    }),
    scoreStudentSpeaking: (stepId: number, data: FormData) => axiosInstance.request<IStudentSpeakingScore>({
        method: 'POST', url: `/student/verbaschola/steps/${stepId}/speaking_score`, data, timeout: 180000,
    }).catch(checkStudentLessonAccess),
    ...createVerbaScholaLessonAiApi(axiosInstance),
    /** Exercise content and student views as Apply would leave them, from an Apply that rolls back. */
    /** The exercises after every proposed change, or only ``change_keys`` (the teacher's version), from an Apply that rolls back. */
    getLessonAiReviewProjection: (lessonId: number, generationId: number, picture_overrides: Record<string, string> = {}, signal?: AbortSignal, change_keys?: string[],
        manual_edits?: { kind: string; id?: number; client_key?: string; fields?: Record<string, unknown> }[]) =>
        axiosInstance.post<IVerbaScholaAiReviewProjection>(`/teacher/verbaschola/lessons/${lessonId}/ai/generations/${generationId}/review_projection`,
            { picture_overrides, ...(change_keys ? { change_keys } : {}), ...(manual_edits?.length ? { manual_edits } : {}) }, { signal }),
    generateLessonImage: (lessonId: number, request: IVerbaScholaExerciseImageRequest) =>
        axiosInstance.post<IVerbaScholaExerciseImageAsset>(`/teacher/verbaschola/lessons/${lessonId}/images/generate`, request, { timeout: 720000 }),
    getLessonImageCandidates: (lessonId: number, assetId: number, sourceImageUrl: string, beforeId?: string) =>
        axiosInstance.get<IVerbaScholaExerciseImageCandidates>(`/teacher/verbaschola/lessons/${lessonId}/images/${assetId}/candidates`,
            { params: { source_image_url: sourceImageUrl, ...(beforeId ? { before_id: beforeId } : {}) } }),
    getRecentLessonImages: (lessonId: number) =>
        axiosInstance.get<{ candidates: IVerbaScholaExerciseImageAsset[] }>(`/teacher/verbaschola/lessons/${lessonId}/images/recent`),
    uploadLessonImage: (lessonId: number, file: File, altText: string, sourceImageUrl?: string) => {
        const data = new FormData();
        data.append('image', file);
        if (altText.trim()) data.append('alt_text', altText.trim());
        if (sourceImageUrl) data.append('source_image_url', sourceImageUrl);
        return axiosInstance.post<IVerbaScholaExerciseImageAsset>(`/teacher/verbaschola/lessons/${lessonId}/images`, data, { timeout: 120000 });
    },
    /** Read one textbook page, sent as a one-page PDF; scanned pages are read by OCR. */
    readPdfPageText: (lessonId: number, page: Blob, { forceOcr = false, signal, pageNumber }: { forceOcr?: boolean; signal?: AbortSignal; pageNumber?: number } = {}) => {
        const data = new FormData();
        data.append('page', page, 'page.pdf');
        // Its PDF page number, for the server's log only.
        if (pageNumber) data.append('page_number', String(pageNumber));
        // A teacher who does not trust a page's own text can have it read by OCR instead.
        if (forceOcr) data.append('force_ocr', '1');
        return axiosInstance.post<IVerbaScholaPdfPageText>(`/teacher/verbaschola/lessons/${lessonId}/pdf_page_text`, data, { timeout: 300000, signal });
    },
    listLessonPdfImports: (lessonId: number) => axiosInstance.get<IVerbaScholaPdfImport[]>(`/teacher/verbaschola/lessons/${lessonId}/pdf_imports`),
    /** Records an import as its pages are added; each warning starts open. It does not change the lesson's revision. */
    createLessonPdfImport: (lessonId: number, data: PdfImportPayload) =>
        axiosInstance.post<IVerbaScholaPdfImport>(`/teacher/verbaschola/lessons/${lessonId}/pdf_imports`, data),
    /** Marks a warning as right on its lines as the teacher sees them now, or opens it again (null). */
    decidePdfImportWarning: (importId: number, warningId: number, decision: PdfImportDecision | null) =>
        axiosInstance.put<IVerbaScholaPdfImport>(`/teacher/verbaschola/pdf_imports/${importId}/decision`, {
            warning_id: warningId, ...(decision ?? {
                decision: 'open', decided_index: null, decided_line: null, decided_unique: null, decided_context: null, decided_version: null,
            }),
        }),
    /** The AI page check: the page's picture and its text as read; nothing is kept. */
    checkPdfPage: (lessonId: number, picture: Blob, text: string, signal?: AbortSignal, pageNumber?: number) => {
        const data = new FormData();
        data.append('picture', picture, 'page.jpg');
        data.append('text', text);
        if (pageNumber) data.append('page_number', String(pageNumber));
        return axiosInstance.post<Omit<IVerbaScholaPdfPageCheck, 'status'> & { status: 'checked' | 'partial'; findings: IVerbaScholaPdfPageFinding[] }>(
            // A little longer than the server waits for the model (verbaschola_pdf_page_check.READ_SECONDS, 9 minutes).
            `/teacher/verbaschola/lessons/${lessonId}/pdf_page_check`, data, { timeout: 600000, signal });
    },
    /** Keeps a page's picture with its import, to look at when settling its warnings later. */
    savePdfImportPicture: (importId: number, pageNumber: number, picture: Blob) => {
        const data = new FormData();
        data.append('picture', picture, 'page.jpg');
        return axiosInstance.put<boolean>(`/teacher/verbaschola/pdf_imports/${importId}/pages/${pageNumber}/picture`, data, { timeout: 120000 });
    },
    /** The AI check of a page made again, on its kept picture and its text now; its findings replace the page's earlier ones. */
    checkPdfImportPageAgain: (importId: number, pageNumber: number, text: string) =>
        axiosInstance.post<IVerbaScholaPdfImport>(`/teacher/verbaschola/pdf_imports/${importId}/pages/${pageNumber}/check`, { text }, { timeout: 600000 }),
    /** The vocabulary and grammar targets the AI reads from the pages' text (as the teacher checked it); nothing is kept. */
    extractPdfTargets: (lessonId: number, data: { pages: { page_number: number; label: string | null; text: string }[]; warnings: string[]; options?: PdfTargetOptions }, signal?: AbortSignal) =>
        // A little longer than the server waits for the model (verbaschola_pdf_targets.READ_SECONDS, 9 minutes).
        axiosInstance.post<IVerbaScholaPdfTargets>(`/teacher/verbaschola/lessons/${lessonId}/pdf_targets`, data, { timeout: 600000, signal }),
    getPdfImportPicture: (importId: number, pageNumber: number) =>
        axiosInstance.get<Blob>(`/teacher/verbaschola/pdf_imports/${importId}/pages/${pageNumber}/picture`, { responseType: 'blob' }),
    /** Drops an import whose pages the saved lesson no longer holds, with its warnings (409 while it still does). */
    forgetPdfImport: (importId: number) => axiosInstance.delete<boolean>(`/teacher/verbaschola/pdf_imports/${importId}`),
    /** forgetPdfImport as the page is left (reloaded or closed): a keepalive request outlives the page; its answer is not seen. */
    forgetPdfImportOnLeave: (importId: number) => {
        const jwt = localStorage.getItem('jwt');
        void fetch(`${REST_HOSTNAME}/teacher/verbaschola/pdf_imports/${importId}`, { method: 'DELETE', keepalive: true, headers: jwt ? { 'x-access-token': jwt } : {} })
            .catch(() => undefined);
    },
    getStudentCatalog: () => axiosInstance.get<IVerbaScholaStudentCatalog>('/student/verbaschola/courses'),
    getStudentFeedback: () => axiosInstance.get<IVerbaScholaStudentFeedback>('/student/verbaschola/feedback'),
    markStudentFeedbackReviewed: (attemptId: number, feedbackSentAt: string) => axiosInstance.put<{ attempt_id: number; student_reviewed_feedback: boolean }>(`/student/verbaschola/feedback/${attemptId}/reviewed`, { feedback_sent_at: feedbackSentAt }),
    getStudentLessonContent: (lessonId: number) => axiosInstance.get<IVerbaScholaStudentLessonContent>(`/student/verbaschola/lessons/${lessonId}/exercise_content`),
    recordStudentActivity: (stepId: number, event_type: 'model_audio_play' | 'content_audio_play' | 'pronunciation_detail_open' | 'short_microphone_hold' | 'activity_exit', duration_ms?: number) =>
        axiosInstance.post<{ recorded: boolean }>(`/student/verbaschola/steps/${stepId}/activity_events`, { event_type, ...(duration_ms === undefined ? {} : { duration_ms }) }, { timeout: 5000 }),
    createStudentAttempt: (stepId: number, payload: IVerbaScholaStudentAttemptPayload, timeout?: number) => axiosInstance.post<IVerbaScholaStudentAttemptResponse>(`/student/verbaschola/steps/${stepId}/attempts`, payload, { timeout }).catch(checkStudentLessonAccess),
    getTeacherStudentPreview: (stepId: number) => axiosInstance.get<IVerbaScholaStudentStep>(`/teacher/verbaschola/steps/${stepId}/student_preview`),
    createTeacherStudentPreviewAction: (stepId: number, payload: IVerbaScholaStudentAttemptPayload & { preview_state?: IStudentSpeakingState | IStudentFlow['state'] }, timeout?: number) =>
        axiosInstance.post<IVerbaScholaStudentAttemptResponse>(`/teacher/verbaschola/steps/${stepId}/student_preview/actions`, payload, { timeout }),
    getLessonExerciseContent: (lessonId: number) => axiosInstance.get<IVerbaScholaExerciseContent>(`/teacher/verbaschola/lessons/${lessonId}/exercise_content`),
    updateLessonQuiz: (stepId: number, payload: IVerbaScholaQuizEdit) => axiosInstance.put<{ authoring_revision: number }>(`/teacher/verbaschola/steps/${stepId}/quiz`, payload),
    updateLessonSpeaking: (stepId: number, payload: IVerbaScholaSpeakingEdit) => axiosInstance.put<{ authoring_revision: number }>(`/teacher/verbaschola/steps/${stepId}/speaking`, payload),
    updateLessonConversation: (stepId: number, payload: IVerbaScholaConversationEdit) => axiosInstance.put<{ authoring_revision: number }>(`/teacher/verbaschola/steps/${stepId}/conversation`, payload),
    updateLessonTypedAnswers: (stepId: number, payload: IVerbaScholaTypedAnswerEdit) => axiosInstance.put<{ authoring_revision: number }>(`/teacher/verbaschola/steps/${stepId}/typed_answers`, payload),
    getDashboard: function () {
        return axiosInstance.request<IVerbaScholaDashboard>({
            method: 'GET',
            url: `/teacher/verbaschola/dashboard`,
        });
    },
    createCourse: function (data: Partial<IVerbaScholaCourse>) {
        return axiosInstance.request<IVerbaScholaCourse>({
            method: 'POST',
            url: `/teacher/verbaschola/courses`,
            data,
        });
    },
    getCourse: function (courseId: number) {
        return axiosInstance.request<IVerbaScholaCourse>({
            method: 'GET',
            url: `/teacher/verbaschola/courses/${courseId}`,
        });
    },
    updateCourse: function (courseId: number, data: Partial<IVerbaScholaCourse>) {
        return axiosInstance.request<boolean>({
            method: 'PUT',
            url: `/teacher/verbaschola/courses/${courseId}`,
            data,
        });
    },
    getCourseOverview: function (courseId: number) {
        return axiosInstance.request<IVerbaScholaCourseOverview>({
            method: 'GET',
            url: `/teacher/verbaschola/courses/${courseId}/overview`,
        });
    },
    getCourseLessonList: function (courseId: number) {
        return axiosInstance.request<IVerbaScholaLesson[]>({
            method: 'GET',
            url: `/teacher/verbaschola/courses/${courseId}/lessons`,
        });
    },
    createLesson: function (courseId: number, data: Partial<IVerbaScholaLesson>) {
        return axiosInstance.request<IVerbaScholaLesson>({
            method: 'POST',
            url: `/teacher/verbaschola/courses/${courseId}/lessons`,
            data,
        });
    },
    getCourseStudentList: function (courseId: number) {
        return axiosInstance.request<IVerbaScholaStudentCourse[]>({
            method: 'GET',
            url: `/teacher/verbaschola/courses/${courseId}/students`,
        });
    },
    createStudentEnrollment: function (courseId: number, data: Partial<IVerbaScholaStudentCourse>) {
        return axiosInstance.request<IVerbaScholaStudentCourse>({
            method: 'POST',
            url: `/teacher/verbaschola/courses/${courseId}/students`,
            data,
        });
    },
    updateStudentEnrollment: function (studentCourseId: number, data: Partial<IVerbaScholaStudentCourse>) {
        return axiosInstance.request<IVerbaScholaStudentCourseUpdateResponse>({
            method: 'PUT',
            url: `/teacher/verbaschola/student_courses/${studentCourseId}`,
            data,
        });
    },
    resendStudentInvitation: function (studentCourseId: number) {
        return axiosInstance.request<boolean>({
            method: 'POST',
            url: `/teacher/verbaschola/student_courses/${studentCourseId}/resend_invitation`,
        });
    },
    deleteStudentEnrollment: function (studentCourseId: number) {
        return axiosInstance.request<boolean>({
            method: 'DELETE',
            url: `/teacher/verbaschola/student_courses/${studentCourseId}`,
        });
    },
    getCourseTeacherList: function (courseId: number) {
        return axiosInstance.request<IVerbaScholaTeacherCourse[]>({
            method: 'GET',
            url: `/teacher/verbaschola/courses/${courseId}/teachers`,
        });
    },
    createTeacherCourse: function (courseId: number, data: Partial<IVerbaScholaTeacherCourse>) {
        return axiosInstance.request<IVerbaScholaTeacherCourse>({
            method: 'POST',
            url: `/teacher/verbaschola/courses/${courseId}/teachers`,
            data,
        });
    },
    updateTeacherCourse: function (teacherCourseId: number, data: Partial<IVerbaScholaTeacherCourse>) {
        return axiosInstance.request<IVerbaScholaTeacherCourseUpdateResponse>({
            method: 'PUT',
            url: `/teacher/verbaschola/teacher_courses/${teacherCourseId}`,
            data,
        });
    },
    deleteTeacherCourse: function (teacherCourseId: number) {
        return axiosInstance.request<boolean>({
            method: 'DELETE',
            url: `/teacher/verbaschola/teacher_courses/${teacherCourseId}`,
        });
    },
    getLesson: function (lessonId: number) {
        return axiosInstance.request<IVerbaScholaLesson>({
            method: 'GET',
            url: `/teacher/verbaschola/lessons/${lessonId}`,
        });
    },
    updateLesson: function (lessonId: number, data: Omit<Partial<IVerbaScholaLesson>, 'authoring_revision'> & {
        expected_authoring_revision?: number;
        require_playable_exercises?: boolean;
        mark_all_exercises_ready?: boolean;
        target_changes?: IVerbaScholaTargetChanges;
    }) {
        return axiosInstance.request<boolean>({
            method: 'PUT',
            url: `/teacher/verbaschola/lessons/${lessonId}`,
            data,
        });
    },
    duplicateLessonToDraft: function (lessonId: number, expectedAuthoringRevision: number) {
        return axiosInstance.request<IVerbaScholaLesson>({
            method: 'POST',
            url: `/teacher/verbaschola/lessons/${lessonId}/duplicate_to_draft`,
            data: { expected_authoring_revision: expectedAuthoringRevision },
        });
    },
    getLessonAudioReadiness: function (lessonId: number) {
        return axiosInstance.request<IVerbaScholaLessonAudioReadiness>({
            method: 'GET',
            url: `/teacher/verbaschola/lessons/${lessonId}/audio_readiness`,
        });
    },
    getLessonOverview: function (lessonId: number) {
        return axiosInstance.request<IVerbaScholaLessonOverview>({
            method: 'GET',
            url: `/teacher/verbaschola/lessons/${lessonId}/overview`,
        });
    },
    getLessonStudentList: function (lessonId: number) {
        return axiosInstance.request<IVerbaScholaStudentProgress[]>({
            method: 'GET',
            url: `/teacher/verbaschola/lessons/${lessonId}/students`,
        });
    },
    getLessonSpeakingReview: function (lessonId: number) {
        return axiosInstance.request<IVerbaScholaSpeakingReviewAttempt[]>({
            method: 'GET',
            url: `/teacher/verbaschola/lessons/${lessonId}/speaking_review`,
        });
    },
    getAttemptSoundGrades: function (attemptId: number) {
        return axiosInstance.request<IVerbaScholaAttemptSoundGrade[]>({
            method: 'GET',
            url: `/teacher/verbaschola/attempts/${attemptId}/sound_grades`,
        });
    },
    saveTeacherSpeakingReview: function (attemptId: number, data: { teacher_feedback: string | null; send_feedback: boolean; sound_grades?: IVerbaScholaAttemptSoundGrade[] }) {
        return axiosInstance.request<IVerbaScholaSpeakingReviewSaveResponse>({
            method: 'PUT',
            url: `/teacher/verbaschola/attempts/${attemptId}/speaking_review`,
            data,
        });
    },
    regenerateLessonAudio: function (lessonId: number, sentenceIds?: number[], speakerIds?: string[]) {
        return axiosInstance.request<IVerbaScholaAudioRegenerationResponse>({
            method: 'POST',
            url: `/teacher/verbaschola/lessons/${lessonId}/audio/regenerate`,
            data: { ...(sentenceIds ? { sentence_ids: sentenceIds } : {}), ...(speakerIds ? { speaker_ids: speakerIds } : {}) },
        });
    },
    getLessonPlanSectionList: function (lessonId: number) {
        return axiosInstance.request<IVerbaScholaLessonPlanSection[]>({
            method: 'GET',
            url: `/teacher/verbaschola/lessons/${lessonId}/plan_sections`,
        });
    },
    createLessonPlanSection: function (lessonId: number, data: Partial<IVerbaScholaLessonPlanSection> & { expected_authoring_revision?: number }) {
        return axiosInstance.request<IVerbaScholaLessonPlanSection>({
            method: 'POST',
            url: `/teacher/verbaschola/lessons/${lessonId}/plan_sections`,
            data,
        });
    },
    updateLessonPlanSection: function (planSectionId: number, data: Partial<IVerbaScholaLessonPlanSection> & { expected_authoring_revision?: number }) {
        return axiosInstance.request<boolean>({
            method: 'PUT',
            url: `/teacher/verbaschola/plan_sections/${planSectionId}`,
            data,
        });
    },
    deleteLessonPlanSection: function (planSectionId: number, expected_authoring_revision: number) {
        return axiosInstance.request<boolean>({
            method: 'DELETE',
            url: `/teacher/verbaschola/plan_sections/${planSectionId}`,
            params: { expected_authoring_revision },
        });
    },
    /** Marks every plan section and material with text Ready in one revision. */
    markLessonPlanReady: function (lessonId: number, expected_authoring_revision: number) {
        return axiosInstance.request<{ marked: number }>({
            method: 'POST',
            url: `/teacher/verbaschola/lessons/${lessonId}/plan_ready`,
            data: { expected_authoring_revision },
        });
    },
    getLessonVocabularyTargetList: function (lessonId: number) {
        return axiosInstance.request<IVerbaScholaLessonVocabularyTarget[]>({
            method: 'GET',
            url: `/teacher/verbaschola/lessons/${lessonId}/vocabulary_targets`,
        });
    },
    createLessonVocabularyTarget: function (lessonId: number, data: Partial<IVerbaScholaLessonVocabularyTarget> & { expected_authoring_revision?: number }) {
        return axiosInstance.request<IVerbaScholaLessonVocabularyTarget>({
            method: 'POST',
            url: `/teacher/verbaschola/lessons/${lessonId}/vocabulary_targets`,
            data,
        });
    },
    updateLessonVocabularyTarget: function (vocabularyTargetId: number, data: Partial<IVerbaScholaLessonVocabularyTarget> & { expected_authoring_revision?: number }) {
        return axiosInstance.request<boolean>({
            method: 'PUT',
            url: `/teacher/verbaschola/vocabulary_targets/${vocabularyTargetId}`,
            data,
        });
    },
    deleteLessonVocabularyTarget: function (vocabularyTargetId: number, expected_authoring_revision: number) {
        return axiosInstance.request<boolean>({
            method: 'DELETE',
            url: `/teacher/verbaschola/vocabulary_targets/${vocabularyTargetId}`,
            params: { expected_authoring_revision },
        });
    },
    getLessonGrammarTargetList: function (lessonId: number) {
        return axiosInstance.request<IVerbaScholaLessonGrammarTarget[]>({
            method: 'GET',
            url: `/teacher/verbaschola/lessons/${lessonId}/grammar_targets`,
        });
    },
    createLessonGrammarTarget: function (lessonId: number, data: Partial<IVerbaScholaLessonGrammarTarget> & { expected_authoring_revision?: number }) {
        return axiosInstance.request<IVerbaScholaLessonGrammarTarget>({
            method: 'POST',
            url: `/teacher/verbaschola/lessons/${lessonId}/grammar_targets`,
            data,
        });
    },
    updateLessonGrammarTarget: function (grammarTargetId: number, data: Partial<IVerbaScholaLessonGrammarTarget> & { expected_authoring_revision?: number }) {
        return axiosInstance.request<boolean>({
            method: 'PUT',
            url: `/teacher/verbaschola/grammar_targets/${grammarTargetId}`,
            data,
        });
    },
    deleteLessonGrammarTarget: function (grammarTargetId: number, expected_authoring_revision: number) {
        return axiosInstance.request<boolean>({
            method: 'DELETE',
            url: `/teacher/verbaschola/grammar_targets/${grammarTargetId}`,
            params: { expected_authoring_revision },
        });
    },
    getLessonExerciseList: function (lessonId: number) {
        return axiosInstance.request<IVerbaScholaLessonExercise[]>({
            method: 'GET',
            url: `/teacher/verbaschola/lessons/${lessonId}/exercises`,
        });
    },
    getLessonReviewIssues: (lessonId: number) =>
        axiosInstance.get<IVerbaScholaLessonReviewIssues>(`/teacher/verbaschola/lessons/${lessonId}/review_issues`),
    updateLessonReviewIssue: (lessonId: number, issueId: number, status: 'accepted' | 'resolved') =>
        axiosInstance.patch<{ issue: IVerbaScholaLessonReviewIssue }>(
            `/teacher/verbaschola/lessons/${lessonId}/review_issues/${encodeURIComponent(issueId)}`, { status }),
    /** Marks exercises with a visible supported student activity Ready in one revision. */
    markLessonExercisesReady: function (lessonId: number, expected_authoring_revision: number) {
        return axiosInstance.request<{ marked: number }>({
            method: 'POST',
            url: `/teacher/verbaschola/lessons/${lessonId}/exercises_ready`,
            data: { expected_authoring_revision },
        });
    },
    /** Numbers one exercise's complete step list in one revision. */
    reorderExerciseSteps: function (exerciseId: number, data: { expected_authoring_revision?: number; ordered_ids: number[] }) {
        return axiosInstance.request<boolean>({
            method: 'PUT',
            url: `/teacher/verbaschola/exercises/${exerciseId}/steps/order`,
            data,
        });
    },
    /** Saves the lesson's complete exercise order and each exercise's session in one revision. */
    arrangeLessonExercises: function (lessonId: number, data: { expected_authoring_revision?: number; exercises: { id: number; session_number: number | null }[] }) {
        return axiosInstance.request<{ changed: number }>({
            method: 'PUT',
            url: `/teacher/verbaschola/lessons/${lessonId}/exercise_layout`,
            data,
        });
    },
    createLessonExercise: function (lessonId: number, data: Partial<IVerbaScholaLessonExercise>) {
        return axiosInstance.request<IVerbaScholaLessonExercise>({
            method: 'POST',
            url: `/teacher/verbaschola/lessons/${lessonId}/exercises`,
            data,
        });
    },
    updateExercisePracticeTargets: function (exerciseId: number, data: { expected_authoring_revision?: number; vocabulary_target_ids: number[]; grammar_target_ids: number[] }) {
        return axiosInstance.request<{ authoring_revision: number }>({
            method: 'put',
            url: `/teacher/verbaschola/exercises/${exerciseId}/practice_targets`,
            data,
        });
    },
    updateLessonExercise: function (exerciseId: number, data: Partial<IVerbaScholaLessonExercise>) {
        return axiosInstance.request<boolean>({
            method: 'PUT',
            url: `/teacher/verbaschola/exercises/${exerciseId}`,
            data,
        });
    },
    getExercisePictureReview: (exerciseId: number) =>
        axiosInstance.get<IVerbaScholaExercisePictureReview>(`/teacher/verbaschola/exercises/${exerciseId}/picture_review`),
    deleteLessonExercise: function (exerciseId: number, expected_authoring_revision: number) {
        return axiosInstance.request<boolean>({
            method: 'DELETE',
            url: `/teacher/verbaschola/exercises/${exerciseId}`,
            params: { expected_authoring_revision },
        });
    },
    getLessonGeneratedMaterialList: function (lessonId: number) {
        return axiosInstance.request<IVerbaScholaLessonGeneratedMaterial[]>({
            method: 'GET',
            url: `/teacher/verbaschola/lessons/${lessonId}/generated_materials`,
        });
    },
    createLessonGeneratedMaterial: function (lessonId: number, data: Partial<IVerbaScholaLessonGeneratedMaterial> & { expected_authoring_revision?: number }) {
        return axiosInstance.request<IVerbaScholaLessonGeneratedMaterial>({
            method: 'POST',
            url: `/teacher/verbaschola/lessons/${lessonId}/generated_materials`,
            data,
        });
    },
    updateLessonGeneratedMaterial: function (generatedMaterialId: number, data: Partial<IVerbaScholaLessonGeneratedMaterial> & { expected_authoring_revision?: number }) {
        return axiosInstance.request<boolean>({
            method: 'PUT',
            url: `/teacher/verbaschola/generated_materials/${generatedMaterialId}`,
            data,
        });
    },
    deleteLessonGeneratedMaterial: function (generatedMaterialId: number, expected_authoring_revision: number) {
        return axiosInstance.request<boolean>({
            method: 'DELETE',
            url: `/teacher/verbaschola/generated_materials/${generatedMaterialId}`,
            params: { expected_authoring_revision },
        });
    },
    getLessonExerciseStepList: function (exerciseId: number) {
        return axiosInstance.request<IVerbaScholaLessonExerciseStep[]>({
            method: 'GET',
            url: `/teacher/verbaschola/exercises/${exerciseId}/steps`,
        });
    },
    createLessonExerciseStep: function (exerciseId: number, data: Partial<IVerbaScholaLessonExerciseStep>) {
        return axiosInstance.request<IVerbaScholaLessonExerciseStep>({
            method: 'POST',
            url: `/teacher/verbaschola/exercises/${exerciseId}/steps`,
            data,
        });
    },
    updateLessonExerciseStep: function (stepId: number, data: Partial<IVerbaScholaLessonExerciseStep>) {
        return axiosInstance.request<boolean>({
            method: 'PUT',
            url: `/teacher/verbaschola/steps/${stepId}`,
            data,
        });
    },
    deleteLessonExerciseStep: function (stepId: number, expected_authoring_revision: number) {
        return axiosInstance.request<boolean>({
            method: 'DELETE',
            url: `/teacher/verbaschola/steps/${stepId}`,
            params: { expected_authoring_revision },
        });
    },
    createLessonChoice: (stepId: number, data: Partial<IVerbaScholaExerciseChoice> & { sentence_text?: string; sentence_pinyin?: string[]; expected_authoring_revision: number }) =>
        axiosInstance.post<IVerbaScholaExerciseChoice>(`/teacher/verbaschola/steps/${stepId}/choices`, data),
    reorderLessonChoices: (stepId: number, data: { ordered_ids: number[]; expected_authoring_revision: number }) =>
        axiosInstance.put<boolean>(`/teacher/verbaschola/steps/${stepId}/choices/order`, data),
    updateLessonChoice: (choiceId: number, data: Partial<IVerbaScholaExerciseChoice> & { sentence_text?: string; sentence_pinyin?: string[]; expected_authoring_revision: number }) =>
        axiosInstance.put<boolean>(`/teacher/verbaschola/choices/${choiceId}`, data),
    deleteLessonChoice: (choiceId: number, expected_authoring_revision: number) =>
        axiosInstance.delete<boolean>(`/teacher/verbaschola/choices/${choiceId}`, { params: { expected_authoring_revision } }),
    createLessonSentenceLink: (stepId: number, data: { sentence_id?: number; sentence_text?: string; sentence_pinyin?: string[]; order_index: number; speaker_name?: string | null;
        student_response?: string | null; student_response_pinyin?: string[] | null;
        expected_authoring_revision: number }) => axiosInstance.post<IVerbaScholaSpeakingSentence>(`/teacher/verbaschola/steps/${stepId}/sentences`, data),
    createConversationSentenceLink: (conversationId: number, data: { sentence_id?: number; sentence_text?: string; sentence_pinyin?: string[]; order_index: number; speaker_name: string;
        expected_authoring_revision: number }) => axiosInstance.post<IVerbaScholaSpeakingSentence>(`/teacher/verbaschola/step_conversations/${conversationId}/sentences`, data),
    reorderLessonSentences: (stepId: number, data: { ordered_ids: number[]; expected_authoring_revision: number }) =>
        axiosInstance.put<boolean>(`/teacher/verbaschola/steps/${stepId}/sentences/order`, data),
    reorderConversationSentences: (conversationId: number, data: { ordered_ids: number[]; expected_authoring_revision: number }) =>
        axiosInstance.put<boolean>(`/teacher/verbaschola/step_conversations/${conversationId}/sentences/order`, data),
    updateLessonSentenceLink: (id: number, data: { order_index?: number; sentence_id?: number; sentence_text?: string; sentence_pinyin?: string[]; speaker_name?: string | null;
        student_response?: string | null; student_response_pinyin?: string[] | null; expected_authoring_revision: number }) =>
        axiosInstance.put<boolean>(`/teacher/verbaschola/step_sentences/${id}`, data),
    deleteLessonSentenceLink: (id: number, expected_authoring_revision: number) =>
        axiosInstance.delete<boolean>(`/teacher/verbaschola/step_sentences/${id}`, { params: { expected_authoring_revision } }),
    createLessonConversation: (stepId: number, data: { name: string; expected_authoring_revision: number }) =>
        axiosInstance.post<IVerbaScholaExerciseConversation>(`/teacher/verbaschola/steps/${stepId}/conversations`, data),
    updateLessonConversationName: (id: number, data: { name: string; expected_authoring_revision: number }) =>
        axiosInstance.put<boolean>(`/teacher/verbaschola/step_conversations/${id}`, data),
    deleteLessonConversation: (id: number, expected_authoring_revision: number) =>
        axiosInstance.delete<boolean>(`/teacher/verbaschola/step_conversations/${id}`, { params: { expected_authoring_revision } }),
};

export interface IVerbaScholaCheckSideContent { text?: string; image_src_url?: string; image_alt?: string }
export interface IVerbaScholaExerciseChoice {
    id: number; verbaschola_lesson_exercise_step_id: number; order_index: number | null;
    choice_text: string | null; choice_pinyin: string[] | null; sentence_id: number | null;
    sentence_text?: string | null; sentence_pinyin?: string[] | null; sentence_class_name?: string | null;
    audio_retry_scheduled?: boolean; audio_failed?: boolean; audio_status?: 'pending' | 'partial' | 'available' | 'needs_attention';
    audio_voice_failures?: { speaker_id: string; failed_attempts: number; retry_after: string | null; has_playable_audio: boolean; replacement_pending: boolean }[];
    audio_completed_voices?: number; audio_required_voices?: number;
    verbaschola_lesson_vocabulary_target_id: number | null; is_correct: boolean;
    choice_content_json?: {
        image_src_url?: string; image_alt?: string; info?: string; feedback?: string; display_word?: string; english?: string; definition?: string;
        example?: IStudentCheckExample; left?: IVerbaScholaCheckSideContent; right?: IVerbaScholaCheckSideContent;
    };
}
export interface IVerbaScholaSpeakingSentence { id: number; verbaschola_lesson_exercise_step_id: number | null; verbaschola_lesson_exercise_step_conversation_id: number | null; sentence_id: number; order_index: number | null; speaker_name: string | null; text: string; pinyin: string[]; student_response: string | null; student_response_pinyin: string[] | null; sentence_class_name: string; audio_retry_scheduled?: boolean; audio_failed?: boolean; audio_status?: 'pending' | 'partial' | 'available' | 'needs_attention'; audio_completed_voices?: number; audio_required_voices?: number; audio_voice_failures?: { speaker_id: string; failed_attempts: number; retry_after: string | null; has_playable_audio: boolean; replacement_pending: boolean }[] }
export interface IVerbaScholaExerciseConversation { id: number; verbaschola_lesson_exercise_step_id: number; name: string | null }
/** A proposal's exercises and student views as Apply would leave them, from an Apply that rolled back. */
export interface IVerbaScholaAiReviewProjection {
    exercise_content: IVerbaScholaExerciseContent;
    student_steps: Record<string, IVerbaScholaStudentStep>;
    /** Rows the proposal would add, by kind and draft key; their ids exist only in this projection. */
    new_ids: Record<string, Record<string, number>>;
    /** Each saved list's order before any change (``<kind>:<parent kind>:<parent id>`` → row ids), which a reorder of it names. */
    saved_orders?: Record<string, number[]>;
    /** The posted edits (by ``index``) this projection left out, which a save would refuse. */
    skipped_edits?: { index: number; reason: string; fields_changed?: string[]; current?: Record<string, unknown>; message?: string; written?: boolean }[];
}

export interface IVerbaScholaExerciseContent {
    authoring_revision: number;
    exercises: IVerbaScholaLessonExercise[];
    steps: IVerbaScholaLessonExerciseStep[];
    choices: IVerbaScholaExerciseChoice[];
    conversations?: IVerbaScholaExerciseConversation[];
    sentences?: IVerbaScholaSpeakingSentence[];
    practice_sessions?: IVerbaScholaPracticeSession[];
    target_coverage?: IVerbaScholaTargetCoverage;
    practice_targets?: IVerbaScholaExercisePracticeTargets[];
}
export interface IVerbaScholaExercisePracticeTargets { exercise_id: number; explicit?: boolean; vocabulary_target_ids: number[]; grammar_target_ids: number[] }
export interface IVerbaScholaSpeakingEdit { expected_authoring_revision: number; title: string; prompt_text: string; prompt_pinyin: string[]; sentences: { id: number; text: string; pinyin: string[] }[] }
export interface IVerbaScholaConversationEdit { expected_authoring_revision: number; title: string; prompt_text: string; prompt_pinyin: string[]; conversation: { id: number; name: string | null; sentences: { id: number; speaker_name: string; text: string; pinyin: string[] }[] } }
export interface IVerbaScholaTypedAnswerEdit { expected_authoring_revision: number; title: string; prompt_text: string; prompt_pinyin: string[]; sentences: { id: number; student_response: string; student_response_pinyin: string[] }[] }
export interface IVerbaScholaQuizEdit { expected_authoring_revision: number; title: string; prompt_text: string; prompt_pinyin: string[]; choices: { id: number; choice_text: string; choice_pinyin: string[]; is_correct: boolean }[] }
